(function() {
  'use strict';

  // Pull live volume-discount % from Shopify Discounts via an isolated
  // Storefront API cart so PDP pricing never depends on the buyer's real cart.
  const STOREFRONT_API_VERSION = '2025-01';
  const PROBE_TTL_MS = 10 * 60 * 1000;
  const probeMemoryCache = new Map();
  const inFlightProbeRequests = new Map();
  let storefrontAccessTokenCache;

  function probeCacheKey(variantId, qty) {
    return 'pricingProbe:' + variantId + ':' + qty;
  }

  // In theme preview / explicit refresh, skip cache so the latest Shopify Discount
  // is fetched on every load.
  function shouldBypassProbeCache() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('preview_theme_id') || params.has('refresh_pricing')) return true;
    } catch (e) {}
    return false;
  }

  function getCachedProbe(variantId, qty) {
    if (shouldBypassProbeCache()) return null;
    const key = probeCacheKey(variantId, qty);
    if (probeMemoryCache.has(key)) {
      return probeMemoryCache.get(key);
    }
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.timestamp || Date.now() - parsed.timestamp > PROBE_TTL_MS) {
        window.localStorage.removeItem(key);
        return null;
      }
      probeMemoryCache.set(key, parsed);
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function setCachedProbe(variantId, qty, data) {
    const key = probeCacheKey(variantId, qty);
    const entry = Object.assign({}, data, { timestamp: Date.now() });
    probeMemoryCache.set(key, entry);
    if (shouldBypassProbeCache()) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {}
  }

  function getStorefrontAccessToken() {
    if (typeof storefrontAccessTokenCache === 'string' && storefrontAccessTokenCache) {
      return storefrontAccessTokenCache;
    }

    try {
      const featuresScript = document.getElementById('shopify-features');
      if (featuresScript && featuresScript.textContent) {
        const features = JSON.parse(featuresScript.textContent);
        if (features && typeof features.accessToken === 'string' && features.accessToken) {
          storefrontAccessTokenCache = features.accessToken;
          return storefrontAccessTokenCache;
        }
      }
    } catch (e) {}

    try {
      const tokenMeta = document.querySelector('meta[name="shopify-checkout-api-token"]');
      const token = tokenMeta && tokenMeta.getAttribute('content');
      if (typeof token === 'string' && token) {
        storefrontAccessTokenCache = token;
        return storefrontAccessTokenCache;
      }
    } catch (e) {}

    storefrontAccessTokenCache = null;
    return null;
  }

  function amountToCents(amount) {
    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed)) return 0;
    return Math.round(parsed * 100);
  }

  async function probeTierPricing(variantId, qty) {
    const storefrontAccessToken = getStorefrontAccessToken();
    if (!storefrontAccessToken) {
      throw new Error('missing storefront access token');
    }

    const response = await fetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({
        query: `
          mutation CustomBuyboxProbe($merchandiseId: ID!, $quantity: Int!) {
            cartCreate(input: { lines: [{ merchandiseId: $merchandiseId, quantity: $quantity }] }) {
              cart {
                lines(first: 1) {
                  edges {
                    node {
                      quantity
                      cost {
                        totalAmount {
                          amount
                          currencyCode
                        }
                        subtotalAmount {
                          amount
                          currencyCode
                        }
                      }
                      discountAllocations {
                        discountedAmount {
                          amount
                          currencyCode
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        variables: {
          merchandiseId: 'gid://shopify/ProductVariant/' + variantId,
          quantity: qty,
        },
      }),
    });
    if (!response.ok) throw new Error('storefront probe failed (' + response.status + ')');

    const payload = await response.json();
    if (Array.isArray(payload.errors) && payload.errors.length) {
      throw new Error(payload.errors.map((error) => error.message).join('; '));
    }

    const userErrors = payload.data &&
      payload.data.cartCreate &&
      Array.isArray(payload.data.cartCreate.userErrors)
      ? payload.data.cartCreate.userErrors
      : [];

    if (userErrors.length) {
      throw new Error(userErrors.map((error) => error.message).join('; '));
    }

    const probeLine = payload.data &&
      payload.data.cartCreate &&
      payload.data.cartCreate.cart &&
      payload.data.cartCreate.cart.lines &&
      Array.isArray(payload.data.cartCreate.cart.lines.edges)
      ? payload.data.cartCreate.cart.lines.edges[0] && payload.data.cartCreate.cart.lines.edges[0].node
      : null;

    if (!probeLine || !probeLine.cost) {
      throw new Error('storefront probe returned no line cost');
    }

    const original = amountToCents(probeLine.cost.subtotalAmount && probeLine.cost.subtotalAmount.amount);
    const final = amountToCents(probeLine.cost.totalAmount && probeLine.cost.totalAmount.amount);

    console.log('[pricing-probe]', {
      variantId,
      qty,
      original,
      final,
      discount_allocations: probeLine.discountAllocations,
    });

    return {
      originalLinePrice: original,
      finalLinePrice: final,
      discountPercent: original > 0 ? Math.round((1 - final / original) * 1000) / 10 : 0,
    };
  }

  function ensureTierPricing(variantId, qty, options = {}) {
    const forceRefresh = options.forceRefresh === true;
    const key = probeCacheKey(variantId, qty);

    if (!forceRefresh) {
      const cached = getCachedProbe(variantId, qty);
      if (cached) return Promise.resolve(cached);
    }

    if (inFlightProbeRequests.has(key)) {
      return inFlightProbeRequests.get(key);
    }

    const request = probeTierPricing(variantId, qty)
      .then((result) => {
        setCachedProbe(variantId, qty, result);
        return result;
      })
      .catch((err) => {
        console.warn('[pricing-probe] probe failed', err);
        return null;
      })
      .finally(() => {
        inFlightProbeRequests.delete(key);
      });

    inFlightProbeRequests.set(key, request);
    return request;
  }

  function initializeBuybox(wrapper) {
    const blockId = wrapper.dataset.blockId;
    const buybox = wrapper.querySelector('.custom-buybox');

    // Get product data from data attributes
    const productData = {
      id: parseInt(wrapper.dataset.productId),
      handle: wrapper.dataset.productHandle,
      variants: JSON.parse(wrapper.dataset.productVariants),
      price: parseInt(wrapper.dataset.productPrice),
      compareAtPrice: parseInt(wrapper.dataset.productComparePrice),
      weight: parseInt(wrapper.dataset.productWeight),
      weightUnit: wrapper.dataset.productWeightUnit,
      available: wrapper.dataset.productAvailable === 'true'
    };

    // Get configuration from data attributes
    const config = {
      weightUnit: wrapper.dataset.weightUnitDisplay,
      weeksLabel: wrapper.dataset.weeksLabel,
      errors: {
        outOfStock: wrapper.dataset.errorOutOfStock,
        selectInterval: wrapper.dataset.errorSelectInterval,
        addToCartFailed: wrapper.dataset.errorAddToCartFailed
      }
    };

    let state = {
      purchaseType: wrapper.dataset.defaultPurchaseType,
      quantity: 1,
      discount: 0,
      interval: null
    };

    // Cached cart-probe results per quantity → live Shopify Discount line totals.
    const probeResults = {};

    // Initialize
    function init() {
      // Set default quantity and interval for subscription
      if (state.purchaseType === 'subscription') {
        const selectedQuantity = buybox.querySelector(`input[name="subscription-quantity-${blockId}"]:checked`);
        if (selectedQuantity) {
          state.quantity = parseInt(selectedQuantity.value);
          state.discount = parseFloat(selectedQuantity.dataset.discount);
        }

        const selectedInterval = buybox.querySelector(`input[name="subscription-interval-${blockId}"]:checked`);
        if (selectedInterval) {
          state.interval = selectedInterval.value;
        }
      } else {
        const selectedTier = buybox.querySelector(`input[name="volume-tier-${blockId}"]:checked`);
        if (selectedTier) {
          state.quantity = parseInt(selectedTier.value);
          state.discount = parseFloat(selectedTier.dataset.discount);
        }
      }

      setupEventListeners();
      hydrateProbeResultsFromCache();
      updateAllPrices();
      updatePricing();
      // Hydrate quickly from cache/fallback pricing, then always re-check Shopify
      // Discounts in the background because discount rules can change outside
      // the theme and would otherwise leave PDP pricing stale.
      refreshProbedPricing({ forceRefresh: true });
    }

    function getVolumeTierQuantities() {
      const tierInputs = buybox.querySelectorAll('input[name="volume-tier-' + blockId + '"]');
      const seen = new Set();
      const qtys = [];
      tierInputs.forEach((input) => {
        const qty = parseInt(input.value, 10);
        if (!Number.isNaN(qty) && qty >= 2 && !seen.has(qty)) {
          seen.add(qty);
          qtys.push(qty);
        }
      });
      return qtys;
    }

    function hydrateProbeResultsFromCache() {
      const variantId = productData.variants[0].id;
      getVolumeTierQuantities().forEach((qty) => {
        const cached = getCachedProbe(variantId, qty);
        if (cached) {
          probeResults[qty] = cached;
        }
      });
    }

    async function refreshProbedPricing(options = {}) {
      const forceRefresh = options.forceRefresh === true;
      const variantId = productData.variants[0].id;
      const qtys = getVolumeTierQuantities();
      if (!qtys.length) return;

      let updated = false;
      for (const qty of qtys) {
        const cached = forceRefresh ? null : getCachedProbe(variantId, qty);
        if (cached) {
          if (!probeResults[qty]) {
            probeResults[qty] = cached;
            updated = true;
          }
          continue;
        }
        const result = await ensureTierPricing(variantId, qty, { forceRefresh });
        if (result) {
          probeResults[qty] = result;
          updated = true;
        }
      }

      if (updated) {
        updateAllPrices();
        updatePricing();
      }
    }

    function updateAllPrices() {
      // Update subscription quantity prices
      buybox.querySelectorAll('.subscription-quantity-option').forEach(option => {
        const input = option.querySelector('input[type="radio"]');
        if (input) {
          const quantity = parseInt(input.value);
          const discount = parseFloat(input.dataset.discount);
          updateQuantityOptionPrices(option, quantity, discount);
        }
      });

      // Update volume option prices
      buybox.querySelectorAll('.volume-option').forEach(option => {
        const input = option.querySelector('input[type="radio"]');
        if (input) {
          const quantity = parseInt(input.value);
          const discount = parseFloat(input.dataset.discount);
          updateVolumeOptionPrices(option, quantity, discount);
        }
      });
    }

    function getSubscriptionTotalPrice(quantity, discount) {
      const sellingPlanUnitPrice = getSellingPlanUnitPrice(quantity, state.interval);
      if (typeof sellingPlanUnitPrice === 'number' && sellingPlanUnitPrice > 0) {
        return sellingPlanUnitPrice * quantity;
      }

      const basePrice = productData.price;
      if (discount > 0) {
        const rawTotal = basePrice * quantity * (1 - discount / 100);
        return Math.round(rawTotal / 10) * 10;
      }

      return basePrice * quantity;
    }

    function getOneTimeTotalPrice(quantity) {
      const probed = probeResults[quantity];
      if (probed && typeof probed.finalLinePrice === 'number' && probed.finalLinePrice > 0) {
        return probed.finalLinePrice;
      }

      const tierTotal = getTierTotalPrice(quantity);
      if (typeof tierTotal === 'number' && tierTotal > 0) {
        return tierTotal;
      }

      const volumeUnitPrice = getVolumeUnitPrice(quantity);
      if (typeof volumeUnitPrice === 'number' && volumeUnitPrice > 0) {
        return volumeUnitPrice * quantity;
      }

      return productData.price * quantity;
    }

    function getTierTotalPrice(quantity) {
      const tierPricingData = wrapper.dataset.tierPricing || '';
      if (!tierPricingData) {
        return null;
      }

      const entries = tierPricingData.split(',');
      for (const entry of entries) {
        const parts = entry.split('|');
        const tierQty = parseInt(parts[0], 10);
        const tierTotal = parts[1] ? parseInt(parts[1], 10) : null;
        if (tierQty === quantity && tierTotal && tierTotal > 0) {
          return tierTotal;
        }
      }
      return null;
    }

    function updateQuantityOptionPrices(option, quantity, discount) {
      const basePrice = productData.price;
      const originalTotal = basePrice * quantity;
      const totalPrice = getSubscriptionTotalPrice(quantity, discount);
      const savings = originalTotal - totalPrice;

      const priceEl = option.querySelector('.quantity-price');
      const savingsEl = option.querySelector('.quantity-savings');
      const perDayEl = option.querySelector('.quantity-per-day');

      if (priceEl) {
        priceEl.textContent = formatMoney(totalPrice);
      }

      if (savingsEl && savings > 0) {
        savingsEl.textContent = 'Spare ' + formatMoney(savings);
      }

      if (perDayEl) {
        const daysPerJar = parseInt(wrapper.dataset.daysPerJar || '30', 10);
        const totalDays = quantity * daysPerJar;
        if (totalDays > 0) {
          perDayEl.textContent = formatMoney(Math.round(totalPrice / totalDays)) + ' pro Tag';
        }
      }
    }

    function updateVolumeOptionPrices(option, quantity, discount) {
      const basePrice = productData.price;
      const originalTotal = basePrice * quantity;
      const totalPrice = getOneTimeTotalPrice(quantity);

      const daysPerJar = parseInt(wrapper.dataset.daysPerJar || '30', 10);
      const pricePerDay = (quantity * daysPerJar) > 0 ? totalPrice / (quantity * daysPerJar) : 0;

      const priceEl = option.querySelector('.volume-price');
      const comparePriceEl = option.querySelector('.volume-compare-price');
      const perDayEl = option.querySelector('.volume-per-day');

      if (priceEl) {
        priceEl.textContent = formatMoney(totalPrice);
      }

      if (comparePriceEl) {
        if (totalPrice < originalTotal) {
          comparePriceEl.textContent = formatMoney(originalTotal);
          comparePriceEl.classList.remove('hidden');
        } else {
          comparePriceEl.classList.add('hidden');
        }
      }

      if (perDayEl) {
        perDayEl.textContent = formatMoney(Math.round(pricePerDay)) + ' pro Tag';
      }
    }

    function setupEventListeners() {
      // Purchase type toggle
      buybox.querySelectorAll('.purchase-type-option').forEach(btn => {
        btn.addEventListener('click', handlePurchaseTypeChange);
      });

      // Subscription quantity selection
      buybox.querySelectorAll(`input[name="subscription-quantity-${blockId}"]`).forEach(input => {
        input.addEventListener('change', handleSubscriptionQuantityChange);
      });

      // Subscription quantity containers
      buybox.querySelectorAll('.subscription-quantity-option').forEach(label => {
        label.addEventListener('click', function() {
          buybox.querySelectorAll('.subscription-quantity-option').forEach(l => l.classList.remove('selected'));
          this.classList.add('selected');
        });
      });

      // Subscription intervals
      buybox.querySelectorAll(`input[name="subscription-interval-${blockId}"]`).forEach(input => {
        input.addEventListener('change', handleIntervalChange);
      });

      // Subscription interval containers
      buybox.querySelectorAll('.subscription-interval').forEach(label => {
        label.addEventListener('click', function() {
          buybox.querySelectorAll('.subscription-interval').forEach(l => l.classList.remove('selected'));
          this.classList.add('selected');
        });
      });

      // Volume tiers
      buybox.querySelectorAll(`input[name="volume-tier-${blockId}"]`).forEach(input => {
        input.addEventListener('change', handleVolumeChange);
      });

      // Volume option containers
      buybox.querySelectorAll('.volume-option').forEach(label => {
        label.addEventListener('click', function() {
          buybox.querySelectorAll('.volume-option').forEach(l => l.classList.remove('selected'));
          this.classList.add('selected');
        });
      });

      // Add to cart
      const addToCartBtn = document.getElementById(`custom-add-to-cart-${blockId}`);
      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', handleAddToCart);
      }
    }

    function handleSubscriptionQuantityChange(e) {
      state.quantity = parseInt(e.target.value);
      state.discount = parseFloat(e.target.dataset.discount);
      updatePricing();
    }

    function handlePurchaseTypeChange(e) {
      const button = e.currentTarget;
      const type = button.dataset.type;
      state.purchaseType = type;

      // Update active button
      buybox.querySelectorAll('.purchase-type-option').forEach(btn => {
        btn.classList.remove('active');
      });
      button.classList.add('active');

      // Toggle visibility
      const subscriptionOptions = document.getElementById(`subscription-options-${blockId}`);
      const volumeOptions = document.getElementById(`volume-options-${blockId}`);

      if (type === 'subscription') {
        subscriptionOptions.classList.remove('hidden');
        volumeOptions.classList.add('hidden');

        // Get selected subscription quantity
        const selectedQuantity = buybox.querySelector(`input[name="subscription-quantity-${blockId}"]:checked`);
        if (selectedQuantity) {
          state.quantity = parseInt(selectedQuantity.value);
          state.discount = parseFloat(selectedQuantity.dataset.discount);
        }

        // Get selected interval
        const selectedInterval = buybox.querySelector(`input[name="subscription-interval-${blockId}"]:checked`);
        if (selectedInterval) {
          state.interval = selectedInterval.value;
        }
      } else {
        subscriptionOptions.classList.add('hidden');
        volumeOptions.classList.remove('hidden');

        // Get selected volume tier
        const selectedTier = buybox.querySelector(`input[name="volume-tier-${blockId}"]:checked`);
        if (selectedTier) {
          state.quantity = parseInt(selectedTier.value);
          state.discount = parseFloat(selectedTier.dataset.discount);
        }
        state.interval = null;
      }

      updatePricing();
    }

    function handleIntervalChange(e) {
      state.interval = e.target.value;
      updateAllPrices();
      updatePricing();
    }

    function handleVolumeChange(e) {
      state.quantity = parseInt(e.target.value);
      state.discount = parseFloat(e.target.dataset.discount);
      updatePricing();
    }

    function updatePricing() {
      const basePrice = productData.price;
      const comparePrice = productData.compareAtPrice;
      const totalPrice =
        state.purchaseType === 'subscription'
          ? getSubscriptionTotalPrice(state.quantity, state.discount)
          : getOneTimeTotalPrice(state.quantity);
      const totalComparePrice = comparePrice * state.quantity;

      // Update add to cart button prices
      const finalPriceEl = document.getElementById(`final-price-${blockId}`);
      const comparePriceEl = document.getElementById(`compare-price-${blockId}`);

      if (finalPriceEl) {
        finalPriceEl.textContent = formatMoney(totalPrice);
      }

      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(totalComparePrice);
        // Hide compare price if it's the same as the actual final price
        if (totalComparePrice <= totalPrice) {
          comparePriceEl.classList.add('hidden');
        } else {
          comparePriceEl.classList.remove('hidden');
        }
      }

      // Calculate unit price (price per kg)
      const weight = productData.weight || 1000;
      const weightInKg = (weight / 1000) * state.quantity;
      const pricePerKg = weightInKg > 0 ? totalPrice / weightInKg : 0;

      // Update the unit price display on the right side of button
      const unitPriceDisplay = document.getElementById(`unit-price-display-${blockId}`);
      if (unitPriceDisplay) {
        if (pricePerKg > 0) {
          unitPriceDisplay.textContent = formatMoney(Math.round(pricePerKg)) + ' / ' + config.weightUnit;
        } else {
          unitPriceDisplay.textContent = '';
        }
      }
    }

    function formatMoney(cents) {
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }

    function parseIntervalFrequency(intervalValue) {
      const parsedFrequency = parseInt(String(intervalValue || ''), 10);
      return Number.isNaN(parsedFrequency) ? null : parsedFrequency;
    }

    function findSellingPlan(data, quantity, frequency) {
      // data format: "2|8-week|690723422547|2117,3|8-week|690723389779|1992,..."
      const key = quantity + '|' + frequency + '-week';
      const plans = data.split(',');
      for (const plan of plans) {
        if (plan.startsWith(key + '|')) {
          const parts = plan.split('|');
          return parts[2];
        }
      }
      return null;
    }

    function findSellingPlanPrice(data, quantity, frequency) {
      // Returns the per-item price in cents from selling plan allocation
      const key = quantity + '|' + frequency + '-week';
      const plans = data.split(',');
      for (const plan of plans) {
        if (plan.startsWith(key + '|')) {
          const parts = plan.split('|');
          return parts[3] ? parseInt(parts[3]) : null;
        }
      }
      return null;
    }

    function getSellingPlanUnitPrice(quantity, intervalValue) {
      const frequency = parseIntervalFrequency(intervalValue);
      if (!frequency) {
        return null;
      }

      const sellingPlansData = wrapper.dataset.sellingPlans || '';
      return findSellingPlanPrice(sellingPlansData, quantity, frequency);
    }

    function getVolumeUnitPrice(quantity) {
      const volumePricingData = wrapper.dataset.volumePricing || '';
      if (!volumePricingData) {
        return null;
      }

      let matchedPrice = null;
      const priceBreaks = volumePricingData.split(',');

      for (const priceBreak of priceBreaks) {
        const parts = priceBreak.split('|');
        const minimumQuantity = parseInt(parts[0], 10);
        const price = parts[1] ? parseInt(parts[1], 10) : null;

        if (!Number.isNaN(minimumQuantity) && price && quantity >= minimumQuantity) {
          matchedPrice = price;
        }
      }

      return matchedPrice;
    }

    async function replaceExistingVolumeBundle() {
      const cartResponse = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to load cart');
      }

      const cartState = await cartResponse.json();
      const existingVolumeBundles = (cartState.items || []).filter((item) => {
        const bundleId = item.properties && item.properties._bundle_id;

        return (
          item.variant_id === productData.variants[0].id &&
          typeof bundleId === 'string' &&
          bundleId.startsWith('volume-')
        );
      });

      for (const item of existingVolumeBundles) {
        const changeConfig = fetchConfig();
        changeConfig.body = JSON.stringify({
          id: item.key,
          quantity: 0,
        });

        const changeResponse = await fetch(routes.cart_change_url, changeConfig);
        if (!changeResponse.ok) {
          throw new Error('Failed to replace volume bundle');
        }
      }
    }

    function delay(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }

    async function getCartState() {
      const response = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to load cart state');
      }

      return response.json();
    }

    function findMatchingCartItem(cartState, options = {}) {
      if (!cartState || !Array.isArray(cartState.items)) {
        return null;
      }

      const {
        variantId,
        purchaseType,
        quantity,
        bundleId,
        subscriptionBundleStep,
      } = options;

      return (
        cartState.items.find((item) => {
          if (item.variant_id !== variantId || item.quantity !== quantity) {
            return false;
          }

          const properties = item.properties || {};

          if (purchaseType === 'onetime') {
            return properties._bundle_id === bundleId;
          }

          if (purchaseType === 'subscription') {
            return String(properties._subscription_bundle_step || '') === String(subscriptionBundleStep || '');
          }

          return false;
        }) || null
      );
    }

    async function waitForSettledCartItem(options = {}) {
      const maxAttempts = options.maxAttempts || 8;
      const attemptDelay = options.attemptDelay || 250;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const cartState = await getCartState();
        const cartItem = findMatchingCartItem(cartState, options);

        if (cartItem) {
          if (
            typeof options.expectedFinalLinePrice !== 'number' ||
            cartItem.final_line_price === options.expectedFinalLinePrice
          ) {
            return cartState;
          }
        }

        await delay(attemptDelay);
      }

      return null;
    }

    async function getFreshCartSections(cart) {
      if (!cart || typeof cart.getSectionsToRender !== 'function') {
        return null;
      }

      const sectionIds = cart
        .getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);

      if (!sectionIds.length) {
        return null;
      }

      // Allow Shopify/app discount logic to settle before re-rendering the cart UI.
      await delay(150);

      const response = await fetch(
        `${routes.cart_url}?sections=${encodeURIComponent(sectionIds.join(','))}`,
        {
          headers: { Accept: 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load fresh cart sections');
      }

      return response.json();
    }

    function cartDrawerSectionsHaveItems(sections) {
      const cartDrawerMarkup = sections && sections['cart-drawer'];
      if (!cartDrawerMarkup) {
        return false;
      }

      const parsedMarkup = new DOMParser().parseFromString(cartDrawerMarkup, 'text/html');
      return Boolean(parsedMarkup.querySelector('.cart-item'));
    }

    function updateCartDrawerSections(cartDrawer, parsedState) {
      if (!cartDrawer || !parsedState || !parsedState.sections) {
        return;
      }

      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
      }

      cartDrawer.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !parsedState.sections[section.id]) {
          return;
        }

        sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    }

    async function handleAddToCart() {
      const button = document.getElementById(`custom-add-to-cart-${blockId}`);
      const errorDiv = document.getElementById(`buybox-error-${blockId}`);

      if (!productData.available) {
        showError(config.errors.outOfStock);
        return;
      }

      if (state.purchaseType === 'subscription' && !state.interval) {
        showError(config.errors.selectInterval);
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cart = cartDrawer || cartNotification;

      button.disabled = true;
      buybox.classList.add('loading');
      errorDiv.classList.remove('show');

      try {
        let data;

        if (state.purchaseType === 'subscription') {
          const formData = new FormData();
          formData.append('id', productData.variants[0].id);
          formData.append('quantity', state.quantity);
          const intervalParts = String(state.interval).split(' ');
          const frequency = parseInt(intervalParts[0]);
          const sellingPlansData = wrapper.dataset.sellingPlans || '';
          const sellingPlanId = findSellingPlan(sellingPlansData, state.quantity, frequency);

          if (!sellingPlanId) {
            throw new Error('Missing selling plan');
          }
          formData.append('selling_plan', sellingPlanId);
          formData.append('properties[_subscription_bundle_step]', state.quantity);
          formData.append('properties[_purchase_type]', 'subscription');
          if (cart) {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id).join(',')
            );
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement(document.activeElement);
          }

          const fetchConf = fetchConfig('javascript');
          fetchConf.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete fetchConf.headers['Content-Type'];
          fetchConf.body = formData;

          const response = await fetch(routes.cart_add_url, fetchConf);
          if (!response.ok) throw new Error('Failed to add to cart');
          data = await response.json();
        } else {
          await replaceExistingVolumeBundle();

          const formData = new FormData();
          formData.append('id', productData.variants[0].id);
          formData.append('quantity', state.quantity);
          formData.append('properties[_bundle_id]', 'volume-' + state.quantity);
          formData.append('properties[_purchase_type]', 'onetime');
          if (cart) {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id).join(',')
            );
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement(document.activeElement);
          }

          const fetchConf = fetchConfig('javascript');
          fetchConf.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete fetchConf.headers['Content-Type'];
          fetchConf.body = formData;

          const response = await fetch(routes.cart_add_url, fetchConf);
          if (!response.ok) throw new Error('Failed to add to cart');
          data = await response.json();
        }

        if (data.status) {
          showError(data.description || data.message || config.errors.addToCartFailed);
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        const drawerInner = cart.querySelector('.drawer__inner');
        if (drawerInner && drawerInner.classList.contains('is-empty')) {
          drawerInner.classList.remove('is-empty');
        }

        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'custom-buybox',
          productVariantId: productData.variants[0].id,
          cartData: data,
        });

        try {
          if (cartDrawer) {
            cartDrawer.renderContents(data);

            const settledCartState = await waitForSettledCartItem({
              variantId: productData.variants[0].id,
              purchaseType: state.purchaseType,
              quantity: state.quantity,
              bundleId: state.purchaseType === 'onetime' ? `volume-${state.quantity}` : null,
              subscriptionBundleStep: state.purchaseType === 'subscription' ? state.quantity : null,
              expectedFinalLinePrice:
                state.purchaseType === 'onetime' ? getOneTimeTotalPrice(state.quantity) : undefined,
            });

            const freshSections = await getFreshCartSections(cartDrawer);
            if (cartDrawerSectionsHaveItems(freshSections)) {
              updateCartDrawerSections(cartDrawer, {
                ...data,
                item_count: settledCartState ? settledCartState.item_count : data.item_count,
                sections: freshSections,
              });
            }
          } else if (cartNotification) {
            cartNotification.renderContents(data);
          }
        } catch (renderError) {
          console.error('renderContents error:', renderError);
          cart.open();
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        showError(config.errors.addToCartFailed);
      } finally {
        button.disabled = false;
        buybox.classList.remove('loading');
      }
    }

    function showError(message) {
      const errorDiv = document.getElementById(`buybox-error-${blockId}`);
      errorDiv.textContent = message;
      errorDiv.classList.add('show');

      setTimeout(() => {
        errorDiv.classList.remove('show');
      }, 5000);
    }

    // Initialize on load
    init();
  }

  // Initialize all buybox instances on the page
  function initializeAllBuyboxes() {
    const buyboxWrappers = document.querySelectorAll('.custom-buybox-wrapper');
    buyboxWrappers.forEach(wrapper => {
      initializeBuybox(wrapper);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllBuyboxes);
  } else {
    initializeAllBuyboxes();
  }
})();
